import { post } from 'axios';
import Card from 'Components/ContractGenerator/layouts/Card';
import CheckMark from 'Components/ContractGenerator/layouts/CheckMark';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import Alert from 'ViatoUi/Extranet/Surfaces/Alert';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Translation from 'ViatoUi/Shared/Utilities/Translation';

import AdditionalFeatures from '../AdditionalFeatures';
import styles from './SelectedProduct.module.scss';

const SelectedProduct = ({ hasButton, buttonSettings }) => {
	const urlPrefix = process.env.NODE_ENV !== 'production' ? '' : 'https://contract-generator.viato.travel';
	const { t } = useTranslation(['contractGenerator', 'translation']);

	const { selectedRooms, selectedContractTermInMonths, selectedProduct, selectedPaymentType } = useSelector(
		contractGeneratorSelectors.getDataToSubmit
	);

	const product = useSelector(contractGeneratorSelectors.getProduct(selectedProduct.id.toString()));

	const selectedOption = selectedProduct.options[product.id] || product.selectedOption;
	const productOption = product.options[selectedOption];

	const [productPriceFull, setProductPriceFull] = useState(null);
	const [productPriceDiscounted, setProductPriceDiscounted] = useState(null);
	const [feePriceFull, setFeePriceFull] = useState(null);
	const [feePriceDiscounted, setFeePriceDiscounted] = useState(null);

	useEffect(() => {
		setProductPriceFull(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: false }
		}).then(response => setProductPriceFull(response.data.price));

		setProductPriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: false }
		}).then(response => setProductPriceDiscounted(response.data.price));

		setFeePriceFull(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: true }
		}).then(response => setFeePriceFull(response.data.price));

		setFeePriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: true }
		}).then(response => setFeePriceDiscounted(response.data.price));
	}, [selectedOption, selectedRooms, selectedPaymentType, selectedContractTermInMonths]);

	return (
		<Card
			buttonSettings={buttonSettings}
			hasButton={hasButton}
			headline={t(`translation:products.${product.key}.title`)}
			priceFooterSettings={{
				feePrice: feePriceFull,
				feePriceDiscounted,
				feeDiscountValue: productOption.setupFee.discount.value,
				feeDiscountType: productOption.setupFee.discount.type,
				price: productPriceFull,
				priceDiscounted: productPriceDiscounted,
				discountValue: productOption.discount.value,
				discountType: productOption.discount.type,
				isSinglePayment: product.isSinglePayment
			}}
			hasPriceFooter
		>
			<FlexBox flexWrap='wrap'>
				{t(`translation:products.${product.key}.infos`).map(infoSet => (
					<div key={infoSet[0]} className={styles.checkmarkWrap}>
						{infoSet.map(info => (
							<CheckMark key={info} text={info} />
						))}
					</div>
				))}
			</FlexBox>

			<AdditionalFeatures infos={t(`translation:products.${product.key}.additionalInfos`)} />

			<Spacer height='l' />
			<Alert display='inline-block' type='bright'>
				<Translation
					components={product?.description?.components || []}
					translationKey={`translation:products.${product.key}.options.${selectedOption}.description`}
					translationNamespace='contractGenerator'
				/>
			</Alert>
		</Card>
	);
};

SelectedProduct.propTypes = {
	hasButton: PropTypes.bool,
	buttonSettings: PropTypes.object
};

SelectedProduct.defaultProps = {
	hasButton: false,
	buttonSettings: {}
};

export default SelectedProduct;
